import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  ArrowLeft, Save, Plus, Trash2, Clock, FileText, 
  CheckCircle, AlertCircle, Pause, Play, Settings,
  Loader2
} from 'lucide-react';
import api from '../services/api';

// Styles personnalisés pour les nœuds
const nodeStyles = {
  action: {
    background: '#3B82F6',
    color: 'white',
    border: '2px solid #2563EB',
  },
  decision: {
    background: '#F59E0B',
    color: 'white',
    border: '2px solid #D97706',
    borderRadius: '8px',
  },
  waiting: {
    background: '#6B7280',
    color: 'white',
    border: '2px solid #4B5563',
  },
  start: {
    background: '#10B981',
    color: 'white',
    border: '2px solid #059669',
  },
  end: {
    background: '#EF4444',
    color: 'white',
    border: '2px solid #DC2626',
  }
};

// Composant de nœud personnalisé avec handles pour les connexions
const CustomNode = ({ data, selected }) => {
  const style = nodeStyles[data.nodeType] || nodeStyles.action;
  const isStart = data.nodeType === 'start';
  const isEnd = data.nodeType === 'end';
  const isDecision = data.nodeType === 'decision';
  
  return (
    <div 
      className={`px-4 py-3 rounded-lg shadow-lg min-w-[150px] relative ${selected ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
      style={style}
    >
      {/* Handle d'entrée HAUT (sauf pour le début) */}
      {!isStart && (
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          className="!w-3 !h-3 !bg-white !border-2 !border-gray-400 hover:!border-blue-500 hover:!bg-blue-100"
          style={{ top: -6 }}
        />
      )}
      
      {/* Handle d'entrée GAUCHE (sauf pour le début) */}
      {!isStart && (
        <Handle
          type="target"
          position={Position.Left}
          id="left-in"
          className="!w-3 !h-3 !bg-white !border-2 !border-gray-400 hover:!border-blue-500 hover:!bg-blue-100"
          style={{ left: -6 }}
        />
      )}
      
      <div className="font-semibold text-sm text-center">{data.label}</div>
      {data.description && (
        <div className="text-xs opacity-90 mt-1 text-center max-w-[180px] line-clamp-2">
          {data.description}
        </div>
      )}
      {data.delai && (
        <div className="flex items-center justify-center gap-1 mt-1 text-xs opacity-80">
          <Clock className="w-3 h-3" />
          <span>{data.delai}j</span>
        </div>
      )}
      {data.documents && data.documents.length > 0 && (
        <div className="flex items-center justify-center gap-1 mt-1 text-xs opacity-80">
          <FileText className="w-3 h-3" />
          <span>{data.documents.length} doc(s)</span>
        </div>
      )}
      
      {/* Handle de sortie BAS (sauf pour la fin) */}
      {!isEnd && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className="!w-3 !h-3 !bg-white !border-2 !border-gray-400 hover:!border-green-500 hover:!bg-green-100"
          style={{ bottom: -6 }}
        />
      )}
      
      {/* Handle de sortie DROITE (sauf pour la fin) */}
      {!isEnd && (
        <Handle
          type="source"
          position={Position.Right}
          id="right-out"
          className="!w-3 !h-3 !bg-white !border-2 !border-gray-400 hover:!border-green-500 hover:!bg-green-100"
          style={{ right: -6 }}
        />
      )}
      
      {/* Handles spéciaux pour les décisions (Oui/Non colorés) */}
      {isDecision && (
        <>
          <Handle
            type="source"
            position={Position.Left}
            id="decision-no"
            className="!w-4 !h-4 !bg-red-500 !border-2 !border-red-600 !-left-2"
            style={{ left: -8, top: '50%', transform: 'translateY(-50%)' }}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="decision-yes"
            className="!w-4 !h-4 !bg-green-500 !border-2 !border-green-600 !-right-2"
            style={{ right: -8, top: '50%', transform: 'translateY(-50%)' }}
          />
        </>
      )}
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

// Composant interne avec ReactFlow hooks
const ParcoursEditor = ({ selectedOrganisme, goBack, loadOrganismes }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [editingNode, setEditingNode] = useState(null);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [newNodeData, setNewNodeData] = useState({
    label: '',
    description: '',
    nodeType: 'action',
    delai: '',
    documents: []
  });
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (selectedOrganisme) {
      loadParcours(selectedOrganisme.id);
    }
  }, [selectedOrganisme]);

  const loadParcours = async (organismeId) => {
    try {
      setLoading(true);
      const res = await api.get(`/parcours/organisme/${organismeId}`);
      
      // Convertir les nœuds pour React Flow
      const flowNodes = (res.data.nodes || []).map(node => ({
        ...node,
        type: 'custom',
        data: {
          ...node.data,
          label: node.data?.label || 'Étape',
          nodeType: node.data?.nodeType || 'action'
        }
      }));
      
      setNodes(flowNodes);
      setEdges(res.data.edges || []);
      
      // Fit view après chargement
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    } catch (error) {
      console.error('Erreur chargement parcours:', error);
      setNodes([]);
      setEdges([]);
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      type: 'smoothstep',
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed }
    }, eds));
  }, [setEdges]);

  const addNode = () => {
    setNewNodeData({
      label: '',
      description: '',
      nodeType: 'action',
      delai: '',
      documents: []
    });
    setEditingNode(null);
    setShowNodeModal(true);
  };

  const saveNode = () => {
    if (!newNodeData.label.trim()) return;

    if (editingNode) {
      // Modifier le nœud existant
      setNodes(nds => nds.map(node => {
        if (node.id === editingNode.id) {
          return {
            ...node,
            data: {
              ...newNodeData,
              label: newNodeData.label
            }
          };
        }
        return node;
      }));
    } else {
      // Créer un nouveau nœud avec position calculée
      const yOffset = nodes.length * 120 + 50;
      const newNode = {
        id: `node_${Date.now()}`,
        type: 'custom',
        position: { x: 250, y: yOffset },
        data: {
          ...newNodeData,
          label: newNodeData.label
        }
      };
      setNodes(nds => [...nds, newNode]);
      
      // Fit view après ajout
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    }

    setShowNodeModal(false);
    setEditingNode(null);
  };

  const onNodeDoubleClick = (event, node) => {
    setEditingNode(node);
    setNewNodeData({
      label: node.data.label || '',
      description: node.data.description || '',
      nodeType: node.data.nodeType || 'action',
      delai: node.data.delai || '',
      documents: node.data.documents || []
    });
    setShowNodeModal(true);
  };

  const deleteSelectedNodes = () => {
    setNodes(nds => nds.filter(node => !node.selected));
    setEdges(eds => eds.filter(edge => {
      const sourceExists = nodes.some(n => n.id === edge.source && !n.selected);
      const targetExists = nodes.some(n => n.id === edge.target && !n.selected);
      return sourceExists && targetExists;
    }));
  };

  const saveParcours = async () => {
    if (!selectedOrganisme) return;

    try {
      setSaving(true);
      
      // Préparer les données à sauvegarder
      const parcoursData = {
        nodes: nodes.map(node => ({
          id: node.id,
          type: node.type,
          position: node.position,
          data: node.data
        })),
        edges: edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          label: edge.label,
          type: edge.type
        }))
      };

      await api.put(`/parcours/organisme/${selectedOrganisme.id}`, parcoursData);
      
      // Mettre à jour la liste des organismes
      loadOrganismes();
      
      alert('Parcours sauvegardé !');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const memoizedNodeTypes = useMemo(() => nodeTypes, []);

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            data-testid="parcours-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-semibold text-gray-900">
              Parcours : {selectedOrganisme.nom}
            </h1>
            <p className="text-sm text-gray-500">
              Double-cliquez sur un bloc pour le modifier
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={addNode}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            data-testid="add-step-btn"
          >
            <Plus className="w-4 h-4" />
            Ajouter étape
          </button>
          <button
            onClick={deleteSelectedNodes}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            data-testid="delete-step-btn"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer
          </button>
          <button
            onClick={saveParcours}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            data-testid="save-parcours-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Canvas React Flow */}
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDoubleClick={onNodeDoubleClick}
          nodeTypes={memoizedNodeTypes}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed }
          }}
        >
          <Controls />
          <MiniMap 
            nodeColor={(node) => {
              const colors = {
                action: '#3B82F6',
                decision: '#F59E0B',
                waiting: '#6B7280',
                start: '#10B981',
                end: '#EF4444'
              };
              return colors[node.data?.nodeType] || '#3B82F6';
            }}
          />
          <Background variant="dots" gap={20} size={1} />
          
          {/* Légende */}
          <Panel position="bottom-left" className="bg-white p-3 rounded-lg shadow-lg border">
            <div className="text-xs font-semibold text-gray-500 mb-2">LÉGENDE</div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span className="text-xs">Début</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500"></div>
                <span className="text-xs">Action</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-500"></div>
                <span className="text-xs">Décision</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-500"></div>
                <span className="text-xs">Attente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="text-xs">Fin</span>
              </div>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Modal édition de nœud */}
      {showNodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-lg">
                {editingNode ? 'Modifier l\'étape' : 'Nouvelle étape'}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'étape *
                </label>
                <input
                  type="text"
                  value={newNodeData.label}
                  onChange={(e) => setNewNodeData({ ...newNodeData, label: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Vérification documents"
                  data-testid="step-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newNodeData.description || ''}
                  onChange={(e) => setNewNodeData({ ...newNodeData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20"
                  placeholder="Décrivez cette étape en détail..."
                  data-testid="step-description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'étape
                </label>
                <select
                  value={newNodeData.nodeType}
                  onChange={(e) => setNewNodeData({ ...newNodeData, nodeType: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  data-testid="step-type-select"
                >
                  <option value="start">🟢 Début</option>
                  <option value="action">🔵 Action</option>
                  <option value="decision">🟠 Décision</option>
                  <option value="waiting">⚫ Attente</option>
                  <option value="end">🔴 Fin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Délai (jours)
                </label>
                <input
                  type="number"
                  value={newNodeData.delai}
                  onChange={(e) => setNewNodeData({ ...newNodeData, delai: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ex: 5"
                  min="0"
                  data-testid="step-delay-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Documents requis (un par ligne)
                </label>
                <textarea
                  value={(newNodeData.documents || []).join('\n')}
                  onChange={(e) => setNewNodeData({ 
                    ...newNodeData, 
                    documents: e.target.value.split('\n').filter(d => d.trim()) 
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24"
                  placeholder="Ex:&#10;Carte d'identité&#10;Justificatif domicile"
                  data-testid="step-documents-input"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => { setShowNodeModal(false); setEditingNode(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                data-testid="cancel-step-btn"
              >
                Annuler
              </button>
              <button
                onClick={saveNode}
                disabled={!newNodeData.label.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="confirm-step-btn"
              >
                {editingNode ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ParcoursOrganismes = () => {
  const [organismes, setOrganismes] = useState([]);
  const [selectedOrganisme, setSelectedOrganisme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrganismes();
  }, []);

  const loadOrganismes = async () => {
    try {
      setLoading(true);
      const res = await api.get('/parcours/organismes/list');
      setOrganismes(res.data);
    } catch (error) {
      console.error('Erreur chargement organismes:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectOrganisme = (organisme) => {
    setSelectedOrganisme(organisme);
  };

  const goBack = () => {
    setSelectedOrganisme(null);
  };

  // Vue liste des organismes
  if (!selectedOrganisme) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen" data-testid="parcours-list">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Parcours Organismes</h1>
            <p className="text-gray-600 mt-1">
              Créez et gérez les workflows visuels pour chaque organisme
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {organismes.map(org => (
                <div
                  key={org.id}
                  onClick={() => selectOrganisme(org)}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-400 hover:shadow-lg transition cursor-pointer group"
                  data-testid={`organisme-card-${org.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600">
                        {org.nom}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {org.has_parcours ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Parcours configuré
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400">
                            <AlertCircle className="w-4 h-4" />
                            Aucun parcours
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                      {org.nom.charAt(0)}
                    </div>
                  </div>
                </div>
              ))}

              {organismes.length === 0 && (
                <div className="col-span-3 text-center py-12 text-gray-500">
                  Aucun organisme trouvé. Ajoutez des caisses dans la configuration.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vue éditeur de parcours avec ReactFlowProvider
  return (
    <ReactFlowProvider>
      <ParcoursEditor 
        selectedOrganisme={selectedOrganisme} 
        goBack={goBack} 
        loadOrganismes={loadOrganismes}
      />
    </ReactFlowProvider>
  );
};

export default ParcoursOrganismes;
